import { jsPDF } from "jspdf";

const PRIMARY = [0, 47, 167]; // #002FA7
const BLACK = [10, 10, 10];
const GRAY = [134, 142, 150];
const GRAY_LIGHT = [222, 226, 230];
const ACCENT = [255, 215, 0];

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

/**
 * Generate a French simplified payslip PDF with company profile (logo, address, SIRET) and salary breakdown.
 * Returns a jsPDF instance — caller should .save() or .output("blob").
 *
 * @param {object} options
 * @param {object} options.entreprise - { name, adresse, ville, code_postal, siret, ape, email, telephone, iban, convention_collective, urssaf, taux_cotisations }
 * @param {string} options.logoDataUrl - base64 dataURL or null
 * @param {object} options.salarie - { code, nom, prenom, poste, contrat, taux_horaire }
 * @param {object} options.periode - { mois, annee }
 * @param {object} options.calcul - { total_heures, jours_travailles, taux_horaire, brut_calcule, primes, retenues, net_a_payer }
 * @param {object} options.breakdown - optional breakdown object (jours conges_paye, etc.)
 */
export function generatePayslipPDF(options) {
  const { entreprise = {}, logoDataUrl, salarie = {}, periode = {}, calcul = {}, breakdown = {} } = options;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  const M = 18;
  let y = 14;

  // Top bar
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, W, 6, "F");
  doc.setFillColor(...ACCENT);
  doc.rect(0, 6, W, 1.5, "F");

  // Logo + Employer block
  y = 16;
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", M, y, 24, 24);
    } catch (e) {
      try { doc.addImage(logoDataUrl, "JPEG", M, y, 24, 24); } catch { /* ignore */ }
    }
  }
  const empX = logoDataUrl ? M + 28 : M;
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(entreprise.name || "Mon Entreprise", empX, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...GRAY);
  let empY = y + 10;
  if (entreprise.adresse) { doc.text(entreprise.adresse, empX, empY); empY += 4; }
  if (entreprise.code_postal || entreprise.ville) {
    doc.text(`${entreprise.code_postal || ""} ${entreprise.ville || ""}`.trim(), empX, empY); empY += 4;
  }
  if (entreprise.siret) { doc.text(`SIRET : ${entreprise.siret}`, empX, empY); empY += 4; }
  if (entreprise.ape) { doc.text(`APE : ${entreprise.ape}`, empX, empY); empY += 4; }

  // Right side: title + period
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("BULLETIN DE PAIE", W - M, y + 5, { align: "right" });
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY);
  const periodLabel = `${MOIS[(periode.mois || 1) - 1]} ${periode.annee || new Date().getFullYear()}`;
  doc.text(periodLabel, W - M, y + 12, { align: "right" });
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("Document indicatif - Édition PAG", W - M, y + 17, { align: "right" });

  y = Math.max(empY, y + 30) + 4;

  // Divider
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.4);
  doc.line(M, y, W - M, y);
  y += 6;

  // Salarie box
  doc.setFillColor(248, 249, 250);
  doc.rect(M, y, W - 2 * M, 22, "F");
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...GRAY);
  doc.text("SALARIÉ", M + 3, y + 4);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLACK);
  doc.text(`${salarie.prenom || ""} ${salarie.nom || ""}`.trim(), M + 3, y + 10);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY);
  doc.text(`Poste : ${salarie.poste || "—"}   |   Contrat : ${salarie.contrat || "—"}   |   Code : ${salarie.code || "—"}`, M + 3, y + 15);
  doc.text(`Taux horaire de référence : ${(calcul.taux_horaire ?? salarie.taux_horaire ?? 0).toFixed(2)} €`, M + 3, y + 19);
  y += 28;

  // Section: éléments de paie
  doc.setFillColor(...BLACK);
  doc.rect(M, y - 4, W - 2 * M, 6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.text("ÉLÉMENTS DE PAIE", M + 2, y);
  y += 8;

  // Table header
  doc.setDrawColor(...GRAY_LIGHT);
  doc.setTextColor(...GRAY);
  doc.setFontSize(7.5);
  doc.text("DÉSIGNATION", M + 2, y);
  doc.text("QUANTITÉ", M + 95, y, { align: "right" });
  doc.text("TAUX", M + 130, y, { align: "right" });
  doc.text("MONTANT", W - M - 2, y, { align: "right" });
  y += 1.5;
  doc.line(M, y, W - M, y);
  y += 5;

  doc.setFontSize(9.5);
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "normal");

  // Lines
  const rows = [];
  const totalHeures = Number(calcul.total_heures || 0);
  const taux = Number(calcul.taux_horaire || salarie.taux_horaire || 0);
  // Hours regular = total - HS25 - HS50
  const hs25 = Number(calcul.hs25 || 0);
  const hs50 = Number(calcul.hs50 || 0);
  const heuresRegulieres = Math.max(0, totalHeures - hs25 - hs50);

  rows.push([
    "Salaire de base",
    `${heuresRegulieres.toFixed(2)} h`,
    `${taux.toFixed(2)} €`,
    `${(heuresRegulieres * taux).toFixed(2)} €`,
  ]);

  if (hs25 > 0) {
    rows.push([
      "Heures supplémentaires +25%",
      `${hs25.toFixed(2)} h`,
      `${(taux * 1.25).toFixed(2)} €`,
      `${(hs25 * taux * 1.25).toFixed(2)} €`,
    ]);
  }
  if (hs50 > 0) {
    rows.push([
      "Heures supplémentaires +50%",
      `${hs50.toFixed(2)} h`,
      `${(taux * 1.5).toFixed(2)} €`,
      `${(hs50 * taux * 1.5).toFixed(2)} €`,
    ]);
  }

  const cpJours = breakdown.jours_conges_paye || 0;
  if (cpJours > 0) rows.push([
    "Indemnité congés payés (CP)",
    `${cpJours} j`,
    "—",
    `${(cpJours * (taux * 7)).toFixed(2)} €`,
  ]);

  // Detailed primes
  const primesDetail = calcul.primes_detail || {};
  const primePanier = Number(primesDetail.panier || 0);
  const primeTransport = Number(primesDetail.transport || 0);
  const primePerf = Number(primesDetail.performance || 0);
  const primeAnciennete = Number(primesDetail.anciennete || 0);
  if (primePanier > 0) rows.push(["Prime panier", "—", "—", `${primePanier.toFixed(2)} €`]);
  if (primeTransport > 0) rows.push(["Indemnité transport", "—", "—", `${primeTransport.toFixed(2)} €`]);
  if (primePerf > 0) rows.push(["Prime de performance", "—", "—", `${primePerf.toFixed(2)} €`]);
  if (primeAnciennete > 0) rows.push(["Prime d'ancienneté", "—", "—", `${primeAnciennete.toFixed(2)} €`]);

  // Legacy single prime field
  const primes = Number(calcul.primes || 0);
  if (primes > 0 && (primePanier + primeTransport + primePerf + primeAnciennete) === 0) {
    rows.push(["Prime", "—", "—", `${primes.toFixed(2)} €`]);
  }

  const retenues = Number(calcul.retenues || 0);
  if (retenues > 0) rows.push(["Retenues / acomptes", "—", "—", `-${retenues.toFixed(2)} €`]);

  rows.forEach(([d, q, t, mt]) => {
    doc.text(d, M + 2, y);
    doc.text(q, M + 95, y, { align: "right" });
    doc.text(t, M + 130, y, { align: "right" });
    doc.text(mt, W - M - 2, y, { align: "right" });
    y += 6;
    doc.setDrawColor(245, 245, 245);
    doc.line(M, y - 3, W - M, y - 3);
  });

  y += 2;

  // Brut box
  const brut = Number(calcul.brut_calcule || (totalHeures * taux + primes + (cpJours * taux * 7) - retenues));
  doc.setFillColor(...PRIMARY);
  doc.rect(M, y, W - 2 * M, 9, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("SALAIRE BRUT", M + 3, y + 6);
  doc.text(`${brut.toFixed(2)} €`, W - M - 3, y + 6, { align: "right" });
  y += 13;

  // Cotisations détaillées (si fournies) ou estimation globale
  const tauxCot = Number(entreprise.taux_cotisations ?? 22);
  const cotisationsDetail = calcul.cotisations_detail || null;

  let cotisations = 0;
  if (cotisationsDetail && Array.isArray(cotisationsDetail) && cotisationsDetail.length > 0) {
    // Détail ligne par ligne
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.setFont("helvetica", "bold");
    doc.text("COTISATIONS SALARIALES (détail)", M + 2, y);
    y += 4;
    doc.setDrawColor(...GRAY_LIGHT);
    doc.line(M, y, W - M, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...BLACK);
    cotisationsDetail.forEach((c) => {
      const m = +(brut * (c.taux / 100)).toFixed(2);
      cotisations += m;
      doc.text(`${c.label} (${c.taux.toFixed(2)}%)`, M + 4, y);
      doc.text(`-${m.toFixed(2)} €`, W - M - 2, y, { align: "right" });
      y += 4.5;
    });
    cotisations = +cotisations.toFixed(2);
    doc.setDrawColor(...GRAY_LIGHT);
    doc.line(M, y, W - M, y);
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.text("Total cotisations salariales", M + 2, y);
    doc.text(`-${cotisations.toFixed(2)} €`, W - M - 2, y, { align: "right" });
    y += 7;
  } else {
    cotisations = +(brut * (tauxCot / 100)).toFixed(2);
    doc.setFontSize(9.5);
    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "normal");
    doc.text(`Cotisations salariales estimées (${tauxCot}%)`, M + 2, y);
    doc.text(`-${cotisations.toFixed(2)} €`, W - M - 2, y, { align: "right" });
    y += 7;
  }

  const netCalc = +(brut - cotisations).toFixed(2);
  const tr = calcul.titres_restaurant;
  if (tr && Number(tr.part_salarie) > 0) {
    doc.setFontSize(9.5);
    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "normal");
    doc.text(`Titres-restaurant — part salariale (${tr.tickets} titre(s) × ${Number(tr.amounts?.valeur_faciale || 0).toFixed(2)} €, part employeur ${Number(tr.part_employeur || 0).toFixed(2)} €)`, M + 2, y);
    doc.text(`-${Number(tr.part_salarie).toFixed(2)} €`, W - M - 2, y, { align: "right" });
    y += 7;
  }
  const net = calcul.net_a_payer != null ? Number(calcul.net_a_payer) : netCalc - Number(tr?.part_salarie || 0);

  // Net box
  doc.setFillColor(...BLACK);
  doc.rect(M, y, W - 2 * M, 11, "F");
  doc.setTextColor(...ACCENT);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("NET À PAYER", M + 3, y + 5);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.text(`${net.toFixed(2)} €`, W - M - 3, y + 7.5, { align: "right" });
  y += 16;

  // Footer mention
  doc.setTextColor(...GRAY);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  const mentionLines = [
    "Bulletin simplifié, à valeur indicative. Les cotisations exactes (Sécurité sociale, retraite, chômage, CSG/CRDS) doivent",
    "être calculées par votre logiciel de paie agréé ou expert-comptable. Document à conserver sans limitation de durée.",
  ];
  mentionLines.forEach((l) => { doc.text(l, M, y); y += 3.5; });

  // Convention / IBAN / etc
  y += 2;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BLACK);
  if (entreprise.convention_collective) { doc.text(`Convention collective : ${entreprise.convention_collective}`, M, y); y += 4; }
  if (entreprise.urssaf) { doc.text(`URSSAF : ${entreprise.urssaf}`, M, y); y += 4; }
  if (entreprise.iban) { doc.text(`Versement sur IBAN : ${entreprise.iban}`, M, y); y += 4; }

  // Signature zone (bas de page)
  const sigY = H - 38;
  doc.setDrawColor(...GRAY_LIGHT);
  doc.rect(M, sigY, 75, 22);
  doc.rect(W - M - 75, sigY, 75, 22);
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  doc.text("Signature de l'employeur", M + 2, sigY + 26);
  doc.text("Signature du salarié", W - M - 75 + 2, sigY + 26);

  // Footer band
  doc.setDrawColor(...BLACK);
  doc.setLineWidth(0.3);
  doc.line(M, H - 12, W - M, H - 12);
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  const contactLine = [entreprise.email, entreprise.telephone].filter(Boolean).join(" · ");
  if (contactLine) doc.text(contactLine, M, H - 8);
  doc.text(`Édité avec PAG Business Manager · ${new Date().toLocaleDateString("fr-FR")}`, W - M, H - 8, { align: "right" });

  return doc;
}
